import { useEffect, useState } from 'react';

import { Heading, Modal } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertyMultiEditWebApiHandler_multi,
  useRentalManagerUserPropertyRestrictionsWebApiHandler_get,
  useRentalManagerUserPropertySummaryWebApiHandler_applications,
  useRentalManagerUserPropertySummaryWebApiHandler_leases,
  useRentalManagerUserPropertySummaryWebApiHandler_payments,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import PropertyConstants from '../../../constants/PropertyConstants';
import EditPropertyType from '../../EditPropertyType';
import ModalFooter from '../ModalFooter';

import type { ChangeEvent } from 'react';
import type { CorrectPropertyTypes } from '../../EditPropertyType';

const { PROPERTY_TYPES_BY_CODE, PROPERTY_CODES_BY_TYPE, LISTING_CODES_BY_TYPE, LISTING_TYPES } = PropertyConstants;

const ROOM_FOR_RENT_CODE = LISTING_CODES_BY_TYPE[LISTING_TYPES.ROOM];

type Props = {
  listingAlias: string;
  onClose: () => void;
};

const EditPropertyTypeModal = ({ listingAlias, onClose }: Props) => {
  const queryClient = useQueryClient();

  const { refetch: refetchRestrictions } = useRentalManagerUserPropertyRestrictionsWebApiHandler_get(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );
  const { refetch: refetchApplicationsSummary } = useRentalManagerUserPropertySummaryWebApiHandler_applications(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );
  const { refetch: refetchPaymentsSummary } = useRentalManagerUserPropertySummaryWebApiHandler_payments(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );
  const { refetch: refetchGetLeasesSummary } = useRentalManagerUserPropertySummaryWebApiHandler_leases(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const { data: overviewData, isLoading } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const { mutate: updatePropertyType } = useRentalManagerUserPropertyMultiEditWebApiHandler_multi();

  const [hasUpdatePropertyTypeError, setHasUpdatePropertyTypeError] = useState(false);
  const [correctPropertyType, setCorrectPropertyType] = useState<CorrectPropertyTypes | undefined>();

  useEffect(() => {
    if (overviewData?.propertyType?.code) {
      setCorrectPropertyType(PROPERTY_TYPES_BY_CODE[overviewData.propertyType.code] as CorrectPropertyTypes);
    }
  }, [overviewData]);

  if (isLoading || !overviewData) {
    return null;
  }

  const propertyTypeCode = overviewData.propertyType?.code;
  const isRoomForRent = overviewData.listingType?.code === ROOM_FOR_RENT_CODE;

  const invalidateAndRefetchQueries = () => {
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertyRestrictionsWebApiHandler_get.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertySummaryWebApiHandler_leases.getKey({ listingAlias }),
    });

    refetchRestrictions();
    refetchApplicationsSummary();
    refetchGetLeasesSummary();
    refetchPaymentsSummary();
  };

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setHasUpdatePropertyTypeError(false);
    setCorrectPropertyType(e.target.value as CorrectPropertyTypes);
  };

  const handleEditPropertySubmit = () => {
    const newPropertyTypeCode = PROPERTY_CODES_BY_TYPE[correctPropertyType as CorrectPropertyTypes];

    updatePropertyType(
      {
        postData: {
          property: {
            listingAlias,
            propertyTypeCode: newPropertyTypeCode,
          },
        },
      },
      {
        onSuccess: () => {
          invalidateAndRefetchQueries();
          onClose();
        },
        onError: () => {
          setHasUpdatePropertyTypeError(true);
        },
      },
    );
  };

  return (
    <Modal
      header={
        <Heading level={2} textStyle="heading-xs">
          Edit property type
        </Heading>
      }
      body={
        <>
          <EditPropertyType
            listingAlias={listingAlias}
            onSelectChange={handleSelectChange}
            correctPropertyType={correctPropertyType as CorrectPropertyTypes}
            isRoomForRent={isRoomForRent}
            onConvertComplete={() => {
              invalidateAndRefetchQueries();
              onClose();
            }}
          />
        </>
      }
      footer={
        <ModalFooter
          onSubmit={handleEditPropertySubmit}
          submitText="Save"
          onCancel={onClose}
          hasError={hasUpdatePropertyTypeError}
          isSubmitDisabled={correctPropertyType === PROPERTY_TYPES_BY_CODE[propertyTypeCode]}
        />
      }
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default EditPropertyTypeModal;
